import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { opsApi } from '@/api/ops';
import type { EnvironmentView } from '@/api/opsTypes';
import { useCurrencyDigits } from './currencies';
import { REPLICA } from './live';

const LAST_ENV_KEY = 'anyware.controlplane.environment.v1';

/** Per-viewer convenience only: the last environment picked. Storage can throw — never fatal. */
function readLastEnvironment(): string | null {
  try {
    return localStorage.getItem(LAST_ENV_KEY);
  } catch {
    return null;
  }
}

const envListeners = new Set<() => void>();
let lastEnvironment: string | null = readLastEnvironment();

/** Remembers the environment picked (the topbar picker or a page's own) and tells every reader. */
export function writeLastEnvironment(key: string): void {
  lastEnvironment = key;
  try {
    localStorage.setItem(LAST_ENV_KEY, key);
  } catch {
    /* private window / blocked storage — the URL still carries the choice */
  }
  for (const listener of envListeners) listener();
}

function subscribeLastEnvironment(listener: () => void): () => void {
  envListeners.add(listener);
  return () => envListeners.delete(listener);
}

/** The environment last picked anywhere in the console (the topbar picker's value), or null. */
export function useLastEnvironment(): string | null {
  return useSyncExternalStore(subscribeLastEnvironment, () => lastEnvironment, () => lastEnvironment);
}

/** The environments registry (replica read, `controlplane:hosts:read`). */
export function useEnvironments() {
  return useQuery({
    queryKey: ['ops', 'environments'],
    queryFn: opsApi.environments.list,
    ...REPLICA,
    staleTime: 60_000,
  });
}

/**
 * The environment a per-environment page works on.
 *
 * Kept in the URL (`?env=tavi`) so a link to "Pricing on ndc" opens Pricing on ndc — then the last one
 * picked, then the first enabled environment. Billing and messaging are per environment: two stacks have
 * two price books, and a page must never blur which one it is editing.
 */
export function useSelectedEnvironment(): {
  env: string | null;
  setEnv: (key: string) => void;
  environments: EnvironmentView[];
  current: EnvironmentView | null;
  isLoading: boolean;
  error: unknown;
} {
  const [params, setParams] = useSearchParams();
  const query = useEnvironments();
  const last = useLastEnvironment();
  const environments = useMemo(() => query.data?.environments ?? [], [query.data]);

  const requested = params.get('env');
  const env = useMemo(() => {
    if (environments.length === 0) return requested;
    if (requested && environments.some((e) => e.key === requested)) return requested;
    if (last && environments.some((e) => e.key === last)) return last;
    return (environments.find((e) => e.enabled) ?? environments[0])?.key ?? null;
  }, [environments, requested, last]);

  const setEnv = useCallback(
    (key: string) => {
      writeLastEnvironment(key);
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('env', key);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // Money on every per-environment page is formatted with THAT billing's currency digits, never Intl (C10).
  useCurrencyDigits(env);

  // Pin the resolved default into the URL once, so what the page shows is what a copied link reopens.
  useEffect(() => {
    if (env && requested !== env && environments.length > 0) {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('env', env);
          return next;
        },
        { replace: true },
      );
    }
  }, [env, requested, environments.length, setParams]);

  return {
    env,
    setEnv,
    environments,
    current: environments.find((e) => e.key === env) ?? null,
    isLoading: query.isPending,
    error: query.error,
  };
}
